import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { DataGridColumn } from './types';

export type DataGridInstance = {
    columns: DataGridColumn[];
    isSelectable: boolean;
    isDraggable: boolean;
    disableDrag: boolean;
    disableDragHint?: string;
    showRowBorder: boolean;
    addRow: (id: string, disabled: boolean) => void;
    removeRow: (id: string) => void;
    updateRowChecked: (id: string, checked: boolean) => void;
    getRowChecked: (id: string) => boolean;
    toggleAllRowsChecked: () => void;
    updateRowDisabled: (id: string, disabled: boolean) => void;
    clearAllSelected: () => void;
    canCheckAll: boolean;
    allChecked: boolean;
    someChecked: boolean;
    lastChecked?: string;
    changeLastChecked: (id?: string) => void;
    rows: string[];
};
export const DataGridContext = createContext<DataGridInstance | undefined>(undefined);

export function useDataGridContext(): DataGridInstance {
    const context = useContext(DataGridContext);
    if (context === undefined) {
        throw new Error('useDataGrid must be used within a FilterProvider');
    }
    return context;
}

type InternalRowState = {
    id: string;
    checked: boolean;
    inView: boolean;
    disabled: boolean;
};

export function useDataGrid(
    columns: DataGridColumn[],
    isSelectable: boolean,
    isDraggable: boolean,
    disableDrag: boolean,
    showRowBorder: boolean,
    disableDragHint?: string,
    onSelect?: (ids: string[]) => void
): DataGridInstance {
    const [rows, setRows] = useState<InternalRowState[]>([]);
    const [lastChecked, setLastChecked] = useState<string | undefined>();
    const prevCheckedRef = useRef<string>();

    useEffect(() => {
        const checkedRows = rows.filter((r) => r.checked && r.inView);
        const ids = checkedRows.map((r) => r.id);

        if (ids.join(' ') !== prevCheckedRef.current) {
            prevCheckedRef.current = ids.join(' ');
            onSelect && onSelect(ids);
        }
    }, [rows]);

    function clearAllSelected(): void {
        setRows((rows) => rows.map((row) => ({ ...row, checked: false })));
    }

    function addRow(id: string, disabled: boolean): void {
        setRows((prev) => {
            if (!prev.find((p) => p.id === id)) {
                return [...prev, { id, checked: false, inView: true, disabled }];
            }

            return prev.map((p) => {
                return p.id === id ? { ...p, inView: true, disabled } : p;
            });
        });
    }

    function removeRow(id: string): void {
        setRows((prev) => {
            return prev.map((p) => (p.id === id ? { ...p, inView: false } : p));
        });
    }

    function updateRowChecked(id: string, checked: boolean): void {
        setRows((prev) => {
            return prev.map((p) => (p.id === id ? { ...p, checked: p.disabled ? false : checked } : p));
        });
    }

    function updateRowDisabled(id: string, disabled: boolean): void {
        setRows((prev) => {
            return prev.map((p) => (p.id === id ? { ...p, disabled } : p));
        });
    }

    function toggleAllRowsChecked(): void {
        const unCheckAll = _getInViewRows()
            .filter((r) => !r.disabled)
            .every((r) => r.checked);

        setRows((prev) => {
            return prev.filter((p) => !p.disabled).map((p) => (p.inView ? { ...p, checked: !unCheckAll } : p));
        });
    }

    function getRowChecked(id: string): boolean {
        return _getInViewRows().find((r) => r.id === id)?.checked || false;
    }

    function _getInViewRows(): InternalRowState[] {
        return rows.filter((r) => r.inView);
    }

    function changeLastChecked(id?: string): void {
        setLastChecked(id);
    }

    return {
        columns,
        isSelectable,
        isDraggable,
        showRowBorder,
        disableDrag,
        disableDragHint,
        addRow,
        removeRow,
        updateRowChecked,
        getRowChecked,
        toggleAllRowsChecked,
        clearAllSelected,
        canCheckAll: _getInViewRows().length > 0,
        allChecked: _getInViewRows().length > 0 ? _getInViewRows().every((r) => r.checked) : false,
        someChecked: _getInViewRows().some((r) => r.checked),
        lastChecked,
        changeLastChecked,
        updateRowDisabled,
        rows: rows.map((r) => r.id),
    };
}
